import {CSSProperties, RefObject, useLayoutEffect, useRef, useState} from 'react';
import {autoUpdate, computePosition, flip, getOverflowAncestors, offset, shift, size, type Placement} from '@floating-ui/dom';

export type PanelSide = 'top' | 'bottom';

interface Options {
    /** The side to open on when it fits; the opposite one takes over when it does not. */
    side: PanelSide;
    /** Which edge of the trigger the panel lines up with — logical, so it mirrors under RTL. */
    align?: 'start' | 'end';
    /** `fixed` (the default): viewport coordinates, for a panel portalled to the body.
     *  `absolute`: in the trigger's positioned ancestor, for a panel rendered beside it. */
    strategy?: 'fixed' | 'absolute';
    /** Called on any scroll or resize while open, instead of the panel following its trigger. */
    onHide?: () => void;
}

// Measured hidden, but already under its own strategy: a fixed `min-width: 100%` is the viewport's.
// Constants, so a closed panel's reset is a no-op render.
const HIDDEN: Record<'fixed' | 'absolute', CSSProperties> = {
    fixed: {position: 'fixed', top: 0, left: 0, visibility: 'hidden'},
    absolute: {position: 'absolute', top: 0, left: 0, visibility: 'hidden'},
};

/**
 * Pins a panel to its trigger in viewport coordinates (`position: fixed`, so no scrolling
 * ancestor can clip it) or, with `strategy: 'absolute'`, in its positioned ancestor's — on
 * whichever side has the room, shifted to stay on screen. Returns
 * the style for the panel and the side it actually landed on — for a caret, or a class.
 * The style also carries `--ff-panel-shift`: how far the shift moved the panel's aligned
 * edge off the trigger's, so a caret drawn against that edge can stay over the trigger,
 * and `--ff-panel-max`: the height left on the side it landed on, for a panel too tall for
 * either side to cap a scrolling body with (the panel itself must not scroll: that clips a caret).
 * The same floating-ui the editor tooltips use (useWordTooltip, TooltipPlugin).
 */
export const useFloatingPanel = (
    open: boolean,
    triggerRef: RefObject<HTMLElement | null>,
    panelRef: RefObject<HTMLElement | null>,
    {side, align = 'start', strategy = 'fixed', onHide}: Options,
) => {
    const [style, setStyle] = useState<CSSProperties>(HIDDEN[strategy]);
    const [landed, setLanded] = useState<PanelSide>(side);
    // Read through a ref so a fresh closure every render does not re-place the panel.
    const onHideRef = useRef(onHide);
    onHideRef.current = onHide;

    useLayoutEffect(() => {
        const trigger = triggerRef.current;
        const panel = panelRef.current;
        if (!open || !trigger || !panel) {
            setStyle(HIDDEN[strategy]);
            return;
        }
        let cancelled = false;
        const rtl = getComputedStyle(trigger).direction === 'rtl';
        // floating-ui reads the alignment as logical already (it mirrors it under RTL).
        const placement = `${side}-${align}` as Placement;
        // Room is measured inside the trigger's own scrollers, not the whole viewport: a
        // fixed panel would otherwise count the space under a header the page scrolls
        // beneath as free.
        const boundary = getOverflowAncestors(trigger).filter((el): el is Element => el instanceof Element);
        const place = () => {
            // Per call: autoUpdate can have several in flight, each with its own side.
            let room = 0;
            return computePosition(trigger, panel, {
                placement,
                strategy,
                // Only the side flips: an edge swapped for the other would leave `--ff-panel-shift` at 0
                // and a caret drawn against the aligned edge away from the trigger — `shift` slides it.
                middleware: [
                    offset(8), flip({boundary, padding: 8, flipAlignment: false}), shift({boundary, padding: 8}),
                    size({boundary, padding: 8, apply: ({availableHeight}) => { room = availableHeight; }}),
                ],
            }).then(({x, y, placement: final, middlewareData}) => {
                if (cancelled) return;
                // Physical shift → distance along the inline-end axis (positive when the aligned
                // edge slid inward, away from the viewport edge).
                const shiftX = middlewareData.shift?.x ?? 0;
                const shiftEnd = Math.round(rtl ? -shiftX : shiftX);
                setStyle({position: strategy, top: Math.round(y), left: Math.round(x), '--ff-panel-shift': `${shiftEnd}px`,
                    '--ff-panel-max': `${Math.floor(room)}px`} as CSSProperties);
                setLanded(final.split('-')[0] as PanelSide);
            });
        };
        if (!onHideRef.current) {
            // Follows the trigger: re-placed on scroll, resize, and either one changing size.
            const stop = autoUpdate(trigger, panel, place);
            return () => {
                cancelled = true;
                stop();
            };
        }
        place();
        const hide = () => onHideRef.current?.();
        // Scrolling inside the panel itself is the panel's own business.
        const onScroll = (e: Event) => {
            if (!panel.contains(e.target as Node)) hide();
        };
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', hide);
        return () => {
            cancelled = true;
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', hide);
        };
    }, [open, side, align, strategy, triggerRef, panelRef]);

    return {style, side: landed};
};
